import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SendEmailParams {
  type: 'waitlist_confirmation' | 'nursing_home_leads' | 'welcome' | 'claim_request' | 'nursing_home_inquiry' | 'guide_download' | 'inquiry_confirmation' | 'expert_application_confirmation' | 'info_request';
  data: {
    firstName?: string;
    email: string;
    nursingHomes?: string[];
    dashboardUrl?: string;
    userType?: 'family' | 'owner';
    // For claim request emails
    adminName?: string;
    nursingHomeName?: string;
    claimantName?: string;
    claimantEmail?: string;
    claimantRole?: string;
    reviewUrl?: string;
    // For nursing home inquiry emails
    county?: string;
    message?: string;
    userInfo?: string;
    claimLink?: string;
    leadId?: string; // Added leadId type
    // For guide download emails
    guideTitle?: string;
    guideUrl?: string;
    waitlistUrl?: string;
    // For inquiry confirmation emails
    signUpUrl?: string;
    lastName?: string;
    phone?: string;
    // For expert application confirmation emails
    professionalTitle?: string;
    interviewUrl?: string;
  };
}

export function useTransactionalEmail() {
  const [isSending, setIsSending] = useState(false);

  const sendEmail = async ({ type, data }: SendEmailParams): Promise<boolean> => {
    try {
      setIsSending(true);
      console.log(`📧 useTransactionalEmail: Attempting to send ${type} email to ${data.email}...`);
      console.log("📧 Email payload:", { type, data });
      
      // Use Supabase functions invoke method instead of direct fetch
      console.log("📤 Calling supabase.functions.invoke('send-transactional-email')...");
      const { data: responseData, error } = await supabase.functions.invoke(
        'send-transactional-email', 
        {
          body: JSON.stringify({ type, data }),
        }
      );
      
      if (error) {
        console.error('📧 Email API error:', {
          error,
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // Check if it's a domain verification error
        if (error.message?.includes('domain is not verified')) {
          console.error("🚫 Domain verification error detected");
          toast.error("Email domain verification error", {
            description: "Please check the Resend.com domain verification settings."
          });
        } else {
          console.error("❌ General email sending error");
          toast.error("Email sending failed", {
            description: error.message || "Please try again later."
          });
        }
        
        throw new Error(error.message || 'Failed to send email');
      }

      console.log('✅ Email sent successfully:', responseData);
      return true;
    } catch (error: any) {
      console.error('💥 Error in useTransactionalEmail:', {
        error,
        message: error?.message,
        stack: error?.stack
      });
      throw error;
    } finally {
      console.log("🏁 useTransactionalEmail: Setting isSending to false");
      setIsSending(false);
    }
  };

  return { sendEmail, isSending };
}
